<script setup lang="ts">
import { computed } from 'vue'
import { ChevronRight, PanelLeftClose, X } from 'lucide-vue-next'
import {
  SECONDARY_MENU_DATA,
  useNavigation,
  usePermissions,
  buildRouteWithListScopeFromRoute
} from '@/shared'
import { useAppStore } from '@/stores'
import { useRoute, useRouter } from 'vue-router'

const { activeItemLabel, expandedItem, toggleExpand, pathMatchesMenuHref } = useNavigation()
const { isVisible } = usePermissions()
const appStore = useAppStore()
const route = useRoute()
const router = useRouter()

/**
 * Props expected by the component, following SKILL.md conventions
 */
interface Props {
  activeModuleId: string
  /** When true, menu collapses to zero width (desktop toggle). */
  collapsed?: boolean
}

// Define props with explicit defaults for better architectural clarity
const props = withDefaults(defineProps<Props>(), {
  activeModuleId: 'dashboards',
  collapsed: false
})

const emit = defineEmits<{
  (e: 'close-mobile'): void
}>()

/** Active when the current route matches this menu href (or a child path). */
const isItemActive = (item: { label: string; href?: string; active?: boolean }) => {
  if (pathMatchesMenuHref(item.href)) return true
  // Fallback for items without a real href (coming-soon / dashboard placeholders)
  return activeItemLabel.value === item.label || (!activeItemLabel.value && !!item.active)
}

/**
 * Category is open when the user toggled it, or when a child matches the route.
 */
const isExpanded = (item: {
  label: string
  items?: Array<{ href?: string; label: string; active?: boolean }>
}) => {
  if (expandedItem.value === item.label) return true
  return item.items?.some((sub) => isItemActive(sub)) ?? false
}

/**
 * Core Logic: Determines the active module's navigation set from centralized constants
 */
const activeMenu = computed(() => {
  const menu = SECONDARY_MENU_DATA[props.activeModuleId] || SECONDARY_MENU_DATA.dashboards

  if (!isVisible(menu)) {
    return { ...menu, items: [] }
  }

  return {
    ...menu,
    items: menu.items
      .filter((item) => isVisible(item))
      .map((item) => ({
        ...item,
        items: item.items ? item.items.filter((sub) => isVisible(sub)) : undefined
      }))
  }
})

const isDisabled = (item: { disabled?: boolean }) => item.disabled === true

/**
 * Navigates to the selected item. Sidebar highlight and breadcrumbs are updated
 * by useNavigation.syncWithRoute after navigation succeeds (including when blocked
 * by unsaved-changes guards).
 */
const handleItemClick = (item: { label: string; href?: string; disabled?: boolean }) => {
  if (isDisabled(item)) return

  if (!appStore.isSidebarOpen) {
    appStore.isSidebarOpen = true
  }

  const href = item.href as string
  router.push(buildRouteWithListScopeFromRoute(href, route))
}

const handleCategoryClick = (label: string) => {
  if (!appStore.isSidebarOpen) {
    appStore.isSidebarOpen = true
  }
  toggleExpand(label)
}
</script>

<template>
  <aside
    v-if="activeMenu.items.length > 0"
    class="flex h-screen flex-col border-r bg-card shadow-sm transition-all duration-300 ease-in-out overflow-hidden shrink-0"
    :class="
      collapsed
        ? 'w-0 min-w-0 border-r-0 opacity-0 pointer-events-none'
        : 'w-[240px] sm:w-[260px] opacity-100'
    "
  >
    <!-- Module Context Header -->
    <div class="flex h-16 shrink-0 items-center justify-between border-b px-4 sm:px-6">
      <h2 class="text-lg font-bold text-foreground tracking-tight truncate">
        {{ activeMenu.title }}
      </h2>
      <div class="flex items-center gap-1 shrink-0">
        <button
          type="button"
          @click="appStore.toggleSidebar()"
          class="hidden md:inline-flex rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          title="Collapse sidebar"
          aria-label="Collapse sidebar"
        >
          <PanelLeftClose class="h-5 w-5" />
        </button>
        <button
          @click="emit('close-mobile')"
          class="md:hidden rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          aria-label="Close menu"
        >
          <X class="h-5 w-5" />
        </button>
      </div>
    </div>

    <!-- Navigation List: Support nested collapsible categories -->
    <div class="flex-1 overflow-y-auto py-6 app-scrollbar">
      <ul class="space-y-1.5 px-4 text-muted-foreground">
        <li v-for="item in activeMenu.items" :key="item.label">
          <!-- Standard Single-Level Menu Item -->
          <div v-if="!item.items">
            <a
              :href="item.disabled ? undefined : item.href"
              :aria-disabled="item.disabled"
              @click.prevent="handleItemClick(item)"
              :class="[
                'flex items-center rounded-xl px-4 py-3 transition-all duration-200',
                item.disabled
                  ? 'cursor-not-allowed opacity-50 pointer-events-none'
                  : 'cursor-pointer',
                !item.disabled && isItemActive(item)
                  ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
                  : !item.disabled
                    ? 'hover:bg-accent hover:text-accent-foreground'
                    : ''
              ]"
            >
              <span class="text-sm font-semibold">{{ item.label }}</span>
            </a>
          </div>

          <!-- Collapsible Multi-Level Category -->
          <div v-else class="space-y-1">
            <button
              @click="handleCategoryClick(item.label)"
              :class="[
                'flex w-full items-center justify-between rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200',
                isExpanded(item)
                  ? 'text-primary bg-primary/5 dark:bg-primary/10'
                  : 'text-muted-foreground hover:bg-accent'
              ]"
            >
              <span>{{ item.label }}</span>
              <ChevronRight
                :class="[
                  'h-4 w-4 transition-transform duration-300 ease-in-out',
                  isExpanded(item) ? 'rotate-90 text-primary' : 'text-muted-foreground'
                ]"
              />
            </button>

            <!-- Category Item Drawer (Smooth height transition) -->
            <Transition
              enter-active-class="transition-all duration-300 ease-out"
              leave-active-class="transition-all duration-200 ease-in"
              enter-from-class="max-h-0 opacity-0 transform translate-y-[-10px]"
              enter-to-class="max-h-[1000px] opacity-100 transform translate-y-0"
              leave-from-class="max-h-[1000px] opacity-100 transform translate-y-0"
              leave-to-class="max-h-0 opacity-0 transform translate-y-[-10px]"
            >
              <ul
                v-show="isExpanded(item)"
                class="ml-6 mt-1 space-y-1 border-l-2 border-primary/10 pl-3 overflow-hidden py-1"
              >
                <li v-for="subItem in item.items" :key="subItem.label">
                  <a
                    :href="subItem.disabled ? undefined : subItem.href"
                    :aria-disabled="subItem.disabled"
                    @click.prevent="handleItemClick(subItem)"
                    :class="[
                      'block rounded-lg px-3 py-2 text-[13px] leading-5 font-medium transition-all duration-200',
                      subItem.disabled
                        ? 'cursor-not-allowed opacity-50 pointer-events-none'
                        : 'cursor-pointer',
                      !subItem.disabled && isItemActive(subItem)
                        ? 'bg-primary/10 text-primary'
                        : !subItem.disabled
                          ? 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          : 'text-muted-foreground'
                    ]"
                  >
                    {{ subItem.label }}
                  </a>
                </li>
              </ul>
            </Transition>
          </div>
        </li>
      </ul>
    </div>
  </aside>
</template>

<style scoped>
/* Base transition utility for nested menus */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style>
